import React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

// components
import {
  Typography,
  Box,
  Size,
  FlipContent,
  useFlip,
  TogglePosition,
  Breakpoint
} from '@americanexpress/aqx-components';
import CarouselStackedBenefits from './StackedCarousel';
import G157Carousel from 'src/universal/components/Experiments/G157/BusinessValueTilesCarousel';
import { SectionTitle } from '../SectionTitle';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';

// selectors and utils
import {
  getDeltaBenefitsTiles,
  getPdpCardShortName
} from 'page_modules/business-pdp/redux';
import { useBreakpoint } from '@americanexpress/aqx-components';
import removeTagsAndTheirContent from 'service_modules/utils/text-remove-tags';

import styles from './__styles__/index.scss?modules';
import { isG157Variant } from 'src/universal/components/Experiments/G157/redux';

const TileFront = ({ tile, isWiderReserveTile, isFlipTile, isG157 }) => {
  return (
    <Box
      className={clsx(styles.tile, isWiderReserveTile && styles.reserveWiderFront)}
      $border={true}
      theme={{
        rounded: Size.SM
      }}
    >
      <div
        className={clsx(styles.textContainer, {
          [styles.isFlipTile]: isFlipTile && !isWiderReserveTile
        })}
      >
        {tile?.assets?.logo && (
          <img
            className={styles.logo}
            src={tile.assets.logo.src}
            alt={tile.assets.logo.alt}
          />
        )}
        <Typography Component="h3" className={styles.title}>
          {tile.title}
        </Typography>
        <Typography className={styles.description}>{tile.description}</Typography>
      </div>
      <Breakpoint>
        {({ sm }) => {
          const breakpoint = sm ? 'mobile' : 'desktop';

          if (isG157) {
            const imgSrc = tile.assets[breakpoint].src;
            let imgSrcVal = imgSrc;
            if (
              String(imgSrc)
                .toLowerCase()
                .indexOf('delta-reserve-annual-companion.jpg') > -1
            ) {
              imgSrcVal = imgSrc.replace(
                'companion.jpg',
                'companion-no-background.jpg'
              );
            }
            return (
              <div className={styles.imgWrapper}>
                <img src={imgSrcVal} alt="" />
              </div>
            );
          }

          return isWiderReserveTile && !sm ? (
            <div className={styles.widerTileImage}>
              <img src={tile.assets[breakpoint].src} alt="" />
            </div>
          ) : (
            <img
              className={styles.tileImage}
              src={tile.assets[breakpoint].src}
              alt=""
            />
          );
        }}
      </Breakpoint>
    </Box>
  );
};

const TileBack = ({ tile }) => {
  const backDesc = tile.backDescription || tile.description;

  return (
    <Box
      className={styles.tileBack}
      $border={true}
      theme={{
        rounded: Size.SM
      }}
    >
      <Typography Component="h3" className={styles.title}>
        {tile.title}
      </Typography>
      <Typography className={styles.description}>{backDesc}</Typography>
    </Box>
  );
};

const FlippableTile = ({ tile, isWiderReserveTile, index, isG157 }) => {
  const { isFlipped, setIsFlipped, rotation, setRotation } = useFlip();
  const impressionEvent = (tile?.dtmEvents || []).find(
    event => event.tag == 'tileimpression'
  );
  const flipEvent = (tile?.dtmEvents || []).find(event => event.tag == 'tileFlip');
  const flipBackEvent = (tile?.dtmEvents || []).find(
    event => event.tag == 'tileFlipBack'
  );

  const handleFlip = () => {
    sendDTMClickEvent(!isFlipped ? flipEvent : flipBackEvent);
    setIsFlipped(!isFlipped);
    setRotation(r => r + 180);
  };

  const txtCnt = String(tile?.description).length;
  const hasLogo = !!tile?.assets?.logo;
  const isLargeTxt = hasLogo ? txtCnt > 170 : txtCnt > 300;
  const screenreaderText = removeTagsAndTheirContent(tile?.title);

  return (
    <div
      className={clsx(styles.flipTile, styles[`item${index}`], {
        [styles.widerTile]: isWiderReserveTile,
        [styles.isLargeTxt]: isLargeTxt
      })}
    >
      {!isG157 && <LazyScrollMetricTracker scrollDtm={{ ...impressionEvent }} />}

      <FlipContent
        cardMaxHeight={'800px'}
        isFlipped={isFlipped}
        handleFlip={handleFlip}
        rotation={rotation}
        togglePosition={TogglePosition.TopRight}
        ariaLabelFront={`Read more about ${screenreaderText}`}
        ariaLabelBack={`Read less about ${screenreaderText}`}
      >
        <TileFront
          tile={tile}
          isWiderReserveTile={isWiderReserveTile}
          isFlipTile={true}
          isG157={isG157}
        />
        <TileBack tile={tile} />
      </FlipContent>
    </div>
  );
};

const DeltaReserveTiles = ({ deltaBenefitsTiles }) => {
  return (
    <div className={styles.deltaBenefitsTiles}>
      <SectionTitle text={deltaBenefitsTiles.title} />
      <div className={clsx(styles.tiles, styles.isFlipTile)}>
        {deltaBenefitsTiles.tilesContent.map((tile, i) => {
          const isWiderReserveTile = i === 0 || i === 3;
          return (
            <FlippableTile
              key={i}
              tile={tile}
              isWiderReserveTile={isWiderReserveTile}
              index={i}
            />
          );
        })}
      </div>
    </div>
  );
};

const DeltaMobileG157 = ({ deltaBenefitsTiles, shortName }) => {
  const itemHeaderList = [];
  const slideList = (deltaBenefitsTiles?.tilesContent || []).map((tile, i) => {
    itemHeaderList.push(tile.title);
    if (!tile.backDescription) {
      return (
        <TileFront
          key={i}
          tile={tile}
          isWiderReserveTile={false}
          isFlipTile={true}
          isG157={true}
        />
      );
    }

    return (
      <FlippableTile
        key={i}
        tile={tile}
        isWiderReserveTile={false}
        index={i}
        isG157={true}
      />
    );
  });

  return (
    <div className={clsx(styles.isG157, styles[shortName])}>
      <G157Carousel
        slideList={slideList}
        header={deltaBenefitsTiles.title}
        needHeaderBox={false}
        originKey="deltaBenefitsTiles"
        itemHeaderList={itemHeaderList}
      />
    </div>
  );
};

const DeltaBenefitsTiles = ({ shortName, deltaBenefitsTiles, location, isG157 }) => {
  const isDeltaReserve = shortName === 'delta-reserve';
  const isDeltaPlatinum = shortName === 'platinum-delta-skymiles';
  const isDeltaGold = shortName === 'gold-delta-skymiles';

  if (
    (!isDeltaReserve && !isDeltaPlatinum && !isDeltaGold) ||
    (isDeltaReserve && location === 'top') ||
    (!isDeltaReserve && location === 'bottom')
  ) {
    return null;
  }

  const { sm } = useBreakpoint();

  if (sm && isG157) {
    // this is for ALL delta cards
    return (
      <DeltaMobileG157
        deltaBenefitsTiles={deltaBenefitsTiles}
        shortName={shortName}
      />
    );
  }

  if (isDeltaReserve) {
    return <DeltaReserveTiles deltaBenefitsTiles={deltaBenefitsTiles} />;
  }

  if (isDeltaGold || isDeltaPlatinum) {
    return (
      <div className={clsx(styles.deltaBenefitsTiles, styles.carousel)}>
        <SectionTitle text={deltaBenefitsTiles.title} />
        <LazyScrollMetricTracker
          scrollDtm={{ ...deltaBenefitsTiles.impressionDTM }}
        />
        <CarouselStackedBenefits />
      </div>
    );
  }

  return null;
};

const mapStateToProps = state => ({
  shortName: getPdpCardShortName(state),
  deltaBenefitsTiles: getDeltaBenefitsTiles(state),
  isG157: isG157Variant(state)
});

export default connect(mapStateToProps)(DeltaBenefitsTiles);
